import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Eye, Calendar, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { CATEGORIES, formatPrice, timeAgo } from '../data/constants';
import { LOCALES } from '../lib/i18n';
import { useApi } from '../hooks/useApi';
import ListingCard from '../components/ListingCard';
import { Gallery } from '../components/listing/Gallery';
import { SellerCard, QuickMessage, PaymentsAccepted } from '../components/listing/SellerCard';

const NotFound = () => {
  const { t } = useApp();
  return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="listing-not-found">
      <h1 className="text-2xl font-bold">{t('detail.notFound')}</h1>
      <Link to="/marketplace" className="text-[#0F8B5C] mt-4 inline-block">{t('detail.back')}</Link>
    </div>
  );
};

const ListingInfo = ({ listing, catName }) => {
  const { t, currency, lang } = useApp();
  const locale = LOCALES[lang];
  const tags = [
    { id: 'cat', label: catName, cls: 'bg-[#FAF7F2]' },
    { id: 'cond', label: `${t('detail.condition')}: ${t(`cond.${listing.condition}`)}`, cls: 'bg-[#FAF7F2]' },
    ...(listing.diasporaShipping ? [{ id: 'dia', label: t('detail.diasporaShipping'), cls: 'bg-[#0F8B5C]/10 text-[#0A6E47]' }] : []),
  ];
  return (
    <div className="mt-8">
      <h1 className="text-2xl md:text-3xl font-black text-[#1A1A1A] leading-tight" data-testid="listing-title">{listing.title}</h1>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-[#7A7468]">
        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.location}</span>
        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {t('detail.posted')} {timeAgo(listing.postedAt, lang)}</span>
        <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {listing.views} {t('detail.views')}</span>
      </div>
      <div className="mt-6 p-6 rounded-2xl bg-[#FAF7F2] border border-[#EFEAE0]">
        <div className="text-3xl md:text-4xl font-black text-[#0F8B5C]" data-testid="listing-detail-price">{formatPrice(listing.price, listing.currency, currency, locale)}</div>
        <div className="text-xs text-[#7A7468] mt-1">≈ {formatPrice(listing.price, listing.currency, 'XOF', locale)} · {formatPrice(listing.price, listing.currency, 'EUR', locale)} · {formatPrice(listing.price, listing.currency, 'USD', locale)}</div>
      </div>
      <div className="mt-8">
        <h2 className="text-lg font-bold text-[#1A1A1A] mb-3">{t('detail.description')}</h2>
        <p className="text-[#3F3A30] leading-relaxed whitespace-pre-line">{listing.description}</p>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {tags.map((tg) => <span key={tg.id} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tg.cls}`}>{tg.label}</span>)}
      </div>
    </div>
  );
};

const ListingDetail = () => {
  const { id } = useParams();
  const { t, lang } = useApp();
  const { data: listing, loading, error } = useApi(`/listings/${id}`);
  const { data: related } = useApi('/listings', listing ? { cat: listing.category, exclude: listing.id, limit: 4 } : null, { enabled: Boolean(listing) });

  if (loading) return <div className="flex justify-center py-32" data-testid="listing-loading"><Loader2 className="w-8 h-8 animate-spin text-[#0F8B5C]" /></div>;
  if (error || !listing) return <NotFound />;

  const cat = CATEGORIES.find((c) => c.id === listing.category);
  const catName = cat?.name[lang] || cat?.name.en || '';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="listing-detail-page">
      <div className="flex items-center gap-2 text-sm text-[#7A7468] mb-6">
        <Link to="/marketplace" className="hover:text-[#0F8B5C]">Marketplace</Link><span>/</span>
        <Link to={`/marketplace?cat=${listing.category}`} className="hover:text-[#0F8B5C]">{catName}</Link><span>/</span>
        <span className="text-[#1A1A1A] font-medium truncate">{listing.title}</span>
      </div>
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div>
          <Gallery listing={listing} />
          <ListingInfo listing={listing} catName={catName} />
        </div>
        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          <SellerCard seller={listing.seller} />
          <QuickMessage sellerName={listing.seller.name} />
          <PaymentsAccepted />
        </aside>
      </div>
      {related?.length > 0 && (
        <section className="mt-16" data-testid="related-listings">
          <h2 className="text-2xl font-black text-[#1A1A1A] mb-6">{t('detail.related')}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{related.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
        </section>
      )}
    </div>
  );
};

export default ListingDetail;
